/**
 * Enquête n°18 — Adapté de l'Étude de Cas CCS n°12
 * (Enseigner les différents types de cyclomoteurs et critères de choix.)
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const typesCyclosEnquete = {
  slug: 'types-cyclomoteurs',
  themeCode: 'V',
  issueNumber: 18,
  totalIssues: 18,
  series: 'enquetes',
  category: 'Enquête véhicule & choix',
  shortTitle: 'Types de cyclos',
  title: "50 cm³ et 1500 € : le bon choix de cyclo pour la rentrée",
  kicker: 'Enquête n°18 · Série « Témoignages & Sécurité »',
  subtitle:
    "Scooter, cyclo classique, mob, électrique. 2-temps ou 4-temps. Thermique ou ZFE-compatible. 4 types, 4 critères, 1 norme légale (≤ 50 cm³ + ≤ 45 km/h). Avant que tes parents signent, voici comment ne pas se faire avoir.",
  heroImage: 'https://images.unsplash.com/photo-1722217678977-6ab2d1a10b38?w=1600&q=80&auto=format&fit=crop',
  heroImageAlt: "Scooter urbain moderne — illustration du choix d'un cyclomoteur pour la rentrée collège",
  readTimeMin: 7,
  seoDescription:
    "Enquête sur les différents types de cyclomoteurs pour permis AM : scooter, classique, mob, électrique, 2T vs 4T, ZFE. Critères de choix usage/budget/fiabilité/revente.",
  seoKeywords:
    'types cyclomoteurs, scooter, 2T 4T, cyclo électrique, ZFE, Euro 5, choix premier cyclo, R311-1',

  edito: {
    title: "Acheter un cyclo, c'est acheter un usage",
    body: "Tes parents te budgétisent 1 500-2 000 € pour ta rentrée en seconde. Le concessionnaire du coin va te proposer ce qu'il a en stock — pas ce qui te va. <strong>4 grandes familles de cyclos existent</strong>, avec des philosophies opposees : scooter (urbain, plug-and-play), cyclo classique (sportif, mécanique), mob (héritage rural), électrique (centre-ville, ZFE-compatible). Cette enquête condense l'étude de cas CCS n°12 — enseignement des différents types de cyclomoteurs et critères de choix. Le moins cher à l'achat est rarement le moins cher à l'usage : tu sauras pourquoi.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les 4 familles de cyclomoteurs',
      paragraphs: [
        "<strong>1. Le scooter</strong>. Yamaha Aerox, Peugeot Kisbee, MBK Booster. Plancher plat (pieds reposés), transmission CVT automatique (pas de vitesses), démarrage électrique. <strong>Avantages</strong> : facile (pas de boite), confort urbain, position droite, coffre sous-selle. <strong>Inconvénients</strong> : performance limitée en côte, robustesse mécanique moyenne. <strong>Le choix « 80 % débutants ».</strong>",
        "<strong>2. Le cyclo classique (sportif)</strong>. Peugeot 103 SPX, MBK 51, Yamaha TZR50, Aprilia RX 50. Boîte de vitesses manuelle (4 ou 6 rapports), allure moto. <strong>Avantages</strong> : performance accélération, plaisir mécanique, image « vraie moto ». <strong>Inconvénients</strong> : technique plus exigeante, entretien régulier (chaîne, embrayage). <strong>Pour ados pratiquant le 2-roues plaisir</strong>, pas pour le simple A→B utilitaire.",
        "<strong>3. La mob (cyclomoteur à pédales)</strong>. Solex, Peugeot 102, MBK 51. Pédales conservées (démarrage en pédalant ou poussant). <strong>Avantages</strong> : nostalgique, réparable facilement, marché d'occasion vivant. <strong>Inconvénients</strong> : très peu de modèles neufs, performances obsolètes, casiers d'occasion souvent usés ou bricolés. <strong>Pour collectionneur ou pour le contexte rural calme.</strong>",
        "<strong>4. Le cyclo électrique</strong>. Niu UQI, Peugeot e-Streetzone, Super Soco CUx. <strong>Avantages</strong> : silencieux (autorisation ZFE), pas d'huile ni de mélange, coût d'usage très bas (10 cts/100 km), pas d'émissions. <strong>Inconvénients</strong> : autonomie 30-60 km (nécessite recharge nocturne), prix neuf > thermique (1 800-3 500 €), valeur de revente incertaine. <strong>Choix d'avenir, surtout centre-ville et lycées ZFE.</strong>",
      ],
      keyPoints: [
        '1. Scooter : 80 % des débutants',
        '2. Cyclo classique : sportif mécanique',
        '3. Mob : collectionneur ou rural',
        '4. Électrique : urbain + ZFE',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "<strong>2-temps vs 4-temps : la grande différence méconnue.</strong> Les moteurs 2-temps (anciens Booster, MBK 51) brulent un mélange essence + huile (huile ajoutée à la pompe ou réservoir séparé). <strong>Performances supérieures</strong> mais : <strong>consommation +30 %</strong>, <strong>émission de fumée et particules</strong>, <strong>entretien lourd</strong> (segments à changer, calamine). Les moteurs 4-temps (scooters modernes, cyclos récents) sont plus sobres, plus fiables, respectent la norme <strong>Euro 5</strong> (applicable depuis 2020). Conséquence concrète : un Booster 2-temps de 2010 est <strong>interdit dans la ZFE de Paris depuis 2024</strong> et bientôt Lyon, Marseille, Toulouse. À l'achat d'occasion : vérifie toujours la vignette Crit'Air et la norme Euro sur la carte grise.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les 4 critères de décision',
      paragraphs: [
        "<strong>1. Usage réel (pas rêvé)</strong>. Tu fais 3 km de domicile au collège, 95 % du temps seul ? Scooter électrique ou thermique 50. Tu fais 15 km en zone péri-urbaine avec montant ? Cyclo classique avec vitesses. Sois honnête : un cyclo sportif pour 1 km/jour, c'est gaspillé.",
        "<strong>2. Budget total « 1ʳᵉ année »</strong>. Pas seulement le prix d'achat. Ajoute : assurance (300-600 €/an pour un 14-17 ans), entretien (100-200 €/an), équipement (300-500 € : casque + gants + blouson basique), carburant (~6 €/100 km en thermique, ~1 €/100 km en électrique). <strong>Règle : prix achat + 40 % = budget réel</strong>.",
        "<strong>3. Fiabilité et réseau après-vente</strong>. Marques avec réseau dense (Peugeot, MBK, Yamaha, Piaggio) : entretien facile, pièces accessibles. Marques marginales ou chaînes Internet : risque de SAV inexistant, pièces importées à délai. Pour un débutant : marque connue obligatoire.",
        "<strong>4. Valeur de revente</strong>. Tu vends ton cyclo à 16 ans (passage au permis A1 / B). Quelle décote ? Scooter thermique classique : -40 % en 3 ans. Cyclo électrique : -50 à -60 % (marché jeune). Mob d'occasion : -10 % (marché collectionneur). Cyclo sportif débridé : <strong>invendable légalement</strong> (illegal). <strong>Achète ce qui sera revendable</strong>.",
      ],
      keyPoints: [
        '1. Usage réel avant fantasme',
        '2. Budget = prix + 40 %',
        '3. Réseau SAV obligatoire',
        '4. Revente : -40 à -50 % en 3 ans',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Sur Leboncoin, ce Booster débridé à 800 €, c'est une affaire. » <strong>TRAPPE MAJEURE.</strong> Un cyclo débridé acheter sur le marché de l'occasion : 1) <strong>est illégal</strong> (R317-23) — tu commets un délit à chaque sortie, 2) <strong>annule ton assurance</strong> automatiquement (clause d'exclusion légale), 3) <strong>cumule probablement d'autres trafics</strong> (vignette CT falsifiée, carte grise douteuse, pièces volées), 4) est <strong>technique-ment fragilisé</strong> (carburateur trituré, pot « cité » non homologué, embrayage forcé). En cas de chute corporelle : <strong>tu payes tout, casier judiciaire à 14 ans</strong>. Règle absolue : <strong>cyclo d'occasion = uniquement chez concessionnaire agréé avec garantie minimale</strong>. Une mob d'occase chez un particulier inconnu : NON.",
    },
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Pour l'étude de cas n°12, je donne aux candidats un <strong>profil-type à équiper</strong> : « Lycéen, 5 km/jour, budget 1 500 € avec assurance et équipement ». Réflexion collective. Ils répondent souvent « Yamaha Aerox » (cher, peu fiable). La bonne réponse : <strong>scooter thermique d'occasion (3-5 ans) chez concessionnaire Peugeot ou MBK, 800-900 €, garantie 6 mois, contrôle technique à jour, +600 € d'équipement et d'assurance.</strong> Mon conseil clé pour les parents : <strong>ne pas acheter neuf pour un 14 ans</strong>. Une 1ʳᵉ chute fait moins mal sur un cyclo d'occase à 900 € que sur un neuf à 2 200 €. Après 18 mois de pratique propre, le passage au neuf (ou au 125) prend tout son sens. »",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '40 %',
    middle: 'de surcoût',
    label: "entre prix d'achat et budget réel (assurance + entretien + équipement + carburant). Sur 1 500 € de cyclo, prévois 2 100 € total la 1ʳᵉ année.",
    source: 'Fédération Française des Motards en Colère — Budget moyen 2024',
    accent: '#6366f1',
  },

  glossary: [
    { term: 'Cyclomoteur (L1e)', def: 'Catégorie européenne : véhicule à 2 roues motorisé ≤ 50 cm³ (thermique) ou ≤ 4 kW (électrique), ≤ 45 km/h. Permis AM.' },
    { term: '2-temps', def: 'Moteur à combustion complétant un cycle en 2 mouvements de piston. Performance élevée, consommation +30 %, émissions fortes, entretien lourd. Souvent interdit en ZFE.' },
    { term: '4-temps', def: 'Moteur à combustion complétant un cycle en 4 mouvements de piston. Plus sobre, plus fiable, respectant Euro 5 (depuis 2020).' },
    { term: 'ZFE', def: 'Zone à Faibles Émissions. Restriction de circulation pour les véhicules anciens et polluants (Crit’Air 4, 5, non classé). Généralisée dans les grandes villes françaises.' },
    { term: 'CVT', def: 'Continuous Variable Transmission. Boîte de vitesses continue automatique des scooters. Équivalent d\'une boîte automatique voiture.' },
    { term: 'Euro 5', def: 'Norme européenne d\'émissions polluantes 2RM applicable depuis 2020 (cyclomoteurs). Limite drastiquement NOx, CO et particules.' },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "L'étude de cas n°12 traite de l'enseignement des différents types de cyclomoteurs et des critères de choix. Public : 3 candidats AM.",
    points: [
      'Public-cible : 3 candidats permis AM (14-16 ans), choix souvent imposé par budget familial.',
      'Type d\'intervention : enseigner (formation initiale).',
      'Durée : 1 heure — découverte par photos + apports différentes familles + activité comparatif en binômes + QCM.',
      'Méthode : diaporama des 4 familles, comparatif technique 2T vs 4T, profil-type à équiper en binômes, QCM final.',
      "Résultat attendu : l'élève distingue les 4 types, comprend 2T vs 4T, lit les caractéristiques administratives, pondère sur 4 critères (usage/budget/fiabilité/revente).",
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°12 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-12',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur les types de cyclos, 2T/4T, ZFE, critères de choix, sanctions. Réussite minimum : 8/10.",
    themeCode: 'V',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: null,
};

export default typesCyclosEnquete;
